import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAuth } from '@/lib/hooks/useAuth';
import { Lightbulb, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface RegisterFormProps {
  userType: 'filmmaker' | 'studio';
}

export function RegisterForm({ userType }: RegisterFormProps) {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }

    setLoading(true);

    try {
      const { data } = await signUp(
        email.toLowerCase(), 
        password, 
        userType,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim()
        }
      );
      
      if (data) {
        setShowSuccess(true);
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-[#212323] flex flex-col items-center justify-center px-4">
        <div className="flex items-center mb-8">
          <Lightbulb className="h-16 w-16 text-[#F8B500]" />
          <h1 className="text-4xl font-bold text-white ml-4">
            PITCH<span className="text-[#F8B500]">lift</span>
          </h1>
        </div>

        <div className="w-full max-w-md text-center">
          <div className="bg-[#F8B500]/10 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              Account Created Successfully!
            </h2>
            <p className="text-gray-300 mb-6">
              Your account has been created. You can now sign in with your email and password.
            </p>
          </div>

          <Link to="/login">
            <Button
              variant="outline"
              className="w-full py-6 text-lg"
              size="lg"
            >
              Go to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#212323] flex flex-col items-center justify-center px-4">
      <div className="flex items-center mb-8">
        <Lightbulb className="h-16 w-16 text-[#F8B500]" />
        <h1 className="text-4xl font-bold text-white ml-4">
          PITCH<span className="text-[#F8B500]">lift</span>
        </h1>
      </div>

      <h2 className="text-3xl font-bold text-[#F8B500] mb-16">
        SIGN UP
      </h2>

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-12">
          <Button
            variant="outline"
            className="w-48 text-lg py-6 border-2 pointer-events-none"
            size="lg"
          >
            {userType.toUpperCase()}
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-900"
                required
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-900"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="EMAIL"
              className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-900"
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="PASSWORD"
              className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-900"
              required
              minLength={6}
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="CONFIRM PASSWORD"
              className="mt-1 bg-gray-300 text-gray-900 placeholder-gray-900"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full py-6 text-lg bg-[#F8B500] text-gray-900 hover:bg-[#F8B500]/90"
            size="lg"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#F8B500] hover:text-[#F8B500]/80">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}